import {
    ApartmentOutlined,
    BadgeOutlined,
    BuildOutlined,
    BusinessCenterOutlined,
    DirectionsCarOutlined,
    PeopleOutlineRounded,
    SellOutlined,
    SupportAgentOutlined,
} from '@mui/icons-material';
import {FC, ReactNode, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {StatsStrip} from 'src/components/common/StatsStrip';
import {StatsStripTone} from 'src/components/common/StatsStrip/StatsStrip.types';
import {UserTypeEnum} from 'src/enums/common';
import {UserStats} from 'src/types/stats';
import {UserGap, UserStatsStripType} from './UserStatsStrip.types';

/**
 * The four kinds of account the list shows. Managers and property owners have a page of their own,
 * and the list leaves them out unless asked, so a tile for them would count rows nobody can reach.
 */
const DEFAULT_TYPES: UserTypeEnum[] = [
    UserTypeEnum.PRIVATE_DRIVER,
    UserTypeEnum.COMPANY,
    UserTypeEnum.EMPLOYEE,
    UserTypeEnum.BUSINESS,
];

const TYPE_ICONS: Record<string, ReactNode> = {
    [UserTypeEnum.PRIVATE_DRIVER]: <DirectionsCarOutlined/>,
    [UserTypeEnum.COMPANY]: <ApartmentOutlined/>,
    [UserTypeEnum.EMPLOYEE]: <BadgeOutlined/>,
    [UserTypeEnum.BUSINESS]: <BusinessCenterOutlined/>,
    // The managers' page is this same strip under another name.
    [UserTypeEnum.TECHNICAL_MANAGER]: <BuildOutlined/>,
    [UserTypeEnum.SALES_MANAGER]: <SellOutlined/>,
    [UserTypeEnum.CLIENT_MANAGER]: <SupportAgentOutlined/>,
};

const TYPE_TONES: Record<string, StatsStripTone> = {
    [UserTypeEnum.PRIVATE_DRIVER]: 'primary',
    [UserTypeEnum.COMPANY]: 'secondary',
    [UserTypeEnum.EMPLOYEE]: 'success',
    [UserTypeEnum.BUSINESS]: 'brand',
    [UserTypeEnum.TECHNICAL_MANAGER]: 'primary',
    [UserTypeEnum.SALES_MANAGER]: 'brand',
    [UserTypeEnum.CLIENT_MANAGER]: 'success',
};

const DEFAULT_GAPS: UserGap[] = ['notActivated', 'awaitingSignature', 'withoutContract'];

/** Three kinds of unfinished, in the order they happen. */
const GAP_CELLS: Record<UserGap, {label: string; tone: StatsStripTone; value: (data?: UserStats) => number | undefined}> = {
    notActivated: {
        label: 'usersNotActivated',
        tone: 'secondary',
        value: (data) => data?.notActivated,
    },
    awaitingSignature: {
        label: 'usersAwaitingSignature',
        tone: 'warning',
        value: (data) => data?.awaitingSignature,
    },
    withoutContract: {
        label: 'usersWithoutContract',
        tone: 'danger',
        value: (data) => data?.withoutContract,
    },
};

export const UserStatsStrip: FC<UserStatsStripType> = ({
                                                           data,
                                                           title = 'users',
                                                           types = DEFAULT_TYPES,
                                                           gaps = DEFAULT_GAPS,
                                                           activeType,
                                                           onSelectType,
                                                           activeGap,
                                                           onSelectGap,
                                                       }) => {
    const intl = useIntl();

    const counts = useMemo(
        () => new Map((data?.types ?? []).map((point) => [point.type, point.users])),
        [data]
    );

    return (
        <StatsStrip
            icon={<PeopleOutlineRounded/>}
            title={title}
            subtitle={intl.formatMessage(
                {id: 'userStatsSubtitle'},
                {users: data?.users ?? 0}
            )}
            pills={gaps.map((gap) => ({
                key: gap,
                label: GAP_CELLS[gap].label,
                value: GAP_CELLS[gap].value(data),
                tone: GAP_CELLS[gap].tone,
                active: activeGap === gap,
                onClick: onSelectGap ? () => onSelectGap(gap) : undefined,
            }))}
            tiles={types.map((type) => ({
                key: type,
                label: type,
                value: counts.get(type),
                icon: TYPE_ICONS[type],
                tone: TYPE_TONES[type],
                active: activeType === type,
                onClick: onSelectType ? () => onSelectType(type) : undefined,
            }))}
        />
    );
};
